/**
 * 保险库条目表单（新建/编辑）：按类型渲染 LOGIN/CARD/NOTE/IDENTITY/WIFI 专属字段。
 * 提交前在内存中组装 VaultItemPayload，交由 hooks 加密（六字段密文），明文不落盘。
 */
import { useEffect, useState } from 'react';
import { useVaultFolders, useCreateVaultItem, useUpdateVaultItem } from '../hooks/useVault';
import type { VaultItemData, VaultItemPayload, VaultItemType } from '../lib/types';
import { VAULT_TYPE_LABELS } from '../lib/utils';
import { PasswordGenerator } from './PasswordGenerator';
import { Button, ErrorBox, Field, Input, Modal, Select, Textarea, Toggle, errMsg } from './ui';

export interface VaultItemEditState {
  id: string;
  folderId: string | null;
  favorite: boolean;
  payload: VaultItemPayload;
}

interface VaultItemFormProps {
  open: boolean;
  onClose: () => void;
  edit?: VaultItemEditState | null; // 传入为编辑模式（payload 为已解密明文）
  defaultType?: VaultItemType;
}

function emptyData(type: VaultItemType): VaultItemData {
  switch (type) {
    case 'LOGIN':
      return { type: 'LOGIN', username: '', password: '' };
    case 'CARD':
      return { type: 'CARD', cardholder: '', number: '' };
    case 'NOTE':
      return { type: 'NOTE', content: '' };
    case 'IDENTITY':
      return { type: 'IDENTITY', fullName: '', idNumber: '' };
    case 'WIFI':
      return { type: 'WIFI', ssid: '', password: '' };
    default:
      return { type: 'NOTE', content: '' };
  }
}

export function VaultItemForm({ open, onClose, edit, defaultType }: VaultItemFormProps) {
  const [type, setType] = useState<VaultItemType>(defaultType ?? 'LOGIN');
  const [name, setName] = useState('');
  const [data, setData] = useState<VaultItemData>(emptyData(defaultType ?? 'LOGIN'));
  const [folderId, setFolderId] = useState('');
  const [favorite, setFavorite] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [showGen, setShowGen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const { data: folders } = useVaultFolders();
  const createItem = useCreateVaultItem();
  const updateItem = useUpdateVaultItem();

  useEffect(() => {
    if (open) {
      if (edit) {
        setType(edit.payload.data.type);
        setName(edit.payload.name);
        setData(edit.payload.data);
        setFolderId(edit.folderId ?? '');
        setFavorite(edit.favorite);
      } else {
        const t = defaultType ?? 'LOGIN';
        setType(t);
        setName('');
        setData(emptyData(t));
        setFolderId('');
        setFavorite(false);
      }
      setError(null);
      setShowPw(false);
      setShowGen(false);
    }
  }, [open, edit, defaultType]);

  // 类型专属字段更新（保持判别联合类型）
  function patchData(patch: Record<string, unknown>) {
    setData((d) => ({ ...d, ...patch }) as VaultItemData);
  }

  async function submit() {
    if (!name.trim()) {
      setError('请填写名称');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const payload: VaultItemPayload = { name: name.trim(), data };
      if (edit) {
        await updateItem.mutateAsync({
          id: edit.id,
          payload,
          folderId: folderId || null,
          favorite,
        });
      } else {
        await createItem.mutateAsync({ payload, folderId: folderId || undefined, favorite });
      }
      onClose();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={edit ? '编辑条目' : '新建条目'}
      wide
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            取消
          </Button>
          <Button onClick={submit} loading={submitting}>
            {edit ? '保存' : '创建'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <ErrorBox error={error} />
        <div className="grid grid-cols-2 gap-3">
          <Field label="类型">
            <Select
              value={type}
              disabled={Boolean(edit)} // 编辑时类型不可变
              onChange={(e) => {
                const t = e.target.value as VaultItemType;
                setType(t);
                setData(emptyData(t));
              }}
            >
              {(Object.keys(VAULT_TYPE_LABELS) as VaultItemType[]).map((t) => (
                <option key={t} value={t}>
                  {VAULT_TYPE_LABELS[t]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="名称" required>
            <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="如：GitHub 主号" />
          </Field>
          <Field label="文件夹">
            <Select value={folderId} onChange={(e) => setFolderId(e.target.value)}>
              <option value="">无</option>
              {(folders ?? []).map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </Select>
          </Field>
          <div className="flex items-end pb-1">
            <Toggle checked={favorite} onChange={setFavorite} label="收藏" />
          </div>
        </div>

        {/* 类型专属字段 */}
        {data.type === 'LOGIN' && (
          <div className="space-y-3">
            <Field label="用户名">
              <Input value={data.username} onChange={(e) => patchData({ username: e.target.value })} />
            </Field>
            <Field label="密码">
              <div className="flex gap-2">
                <Input
                  type={showPw ? 'text' : 'password'}
                  value={data.password}
                  onChange={(e) => patchData({ password: e.target.value })}
                  className="font-mono"
                />
                <Button size="sm" variant="ghost" onClick={() => setShowPw((v) => !v)} aria-label="显示/隐藏密码">
                  {showPw ? '隐藏' : '显示'}
                </Button>
              </div>
            </Field>
            <div>
              <button
                type="button"
                className="text-xs text-indigo-600 hover:underline dark:text-indigo-400"
                onClick={() => setShowGen((v) => !v)}
              >
                {showGen ? '收起密码生成器' : '打开密码生成器'}
              </button>
              {showGen && (
                <div className="mt-2">
                  <PasswordGenerator onUse={(pw) => patchData({ password: pw })} />
                </div>
              )}
            </div>
            <Field label="网址">
              <Input value={data.url ?? ''} onChange={(e) => patchData({ url: e.target.value })} />
            </Field>
            <Field label="备注">
              <Textarea rows={2} value={data.note ?? ''} onChange={(e) => patchData({ note: e.target.value })} />
            </Field>
          </div>
        )}

        {data.type === 'CARD' && (
          <div className="space-y-3">
            <Field label="持卡人">
              <Input value={data.cardholder} onChange={(e) => patchData({ cardholder: e.target.value })} />
            </Field>
            <Field label="卡号">
              <Input value={data.number} onChange={(e) => patchData({ number: e.target.value })} className="font-mono" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="有效期">
                <Input value={data.expiry ?? ''} onChange={(e) => patchData({ expiry: e.target.value })} placeholder="MM/YY" />
              </Field>
              <Field label="CVV">
                <Input
                  type={showPw ? 'text' : 'password'}
                  value={data.cvv ?? ''}
                  onChange={(e) => patchData({ cvv: e.target.value })}
                  className="font-mono"
                />
              </Field>
              <Field label="PIN">
                <Input
                  type={showPw ? 'text' : 'password'}
                  value={data.pin ?? ''}
                  onChange={(e) => patchData({ pin: e.target.value })}
                  className="font-mono"
                />
              </Field>
              <div className="flex items-end pb-1">
                <Button size="sm" variant="ghost" onClick={() => setShowPw((v) => !v)}>
                  {showPw ? '隐藏敏感字段' : '显示敏感字段'}
                </Button>
              </div>
            </div>
            <Field label="备注">
              <Textarea rows={2} value={data.note ?? ''} onChange={(e) => patchData({ note: e.target.value })} />
            </Field>
          </div>
        )}

        {data.type === 'NOTE' && (
          <Field label="内容" required>
            <Textarea rows={8} value={data.content} onChange={(e) => patchData({ content: e.target.value })} />
          </Field>
        )}

        {data.type === 'IDENTITY' && (
          <div className="space-y-3">
            <Field label="姓名">
              <Input value={data.fullName} onChange={(e) => patchData({ fullName: e.target.value })} />
            </Field>
            <Field label="证件号">
              <Input
                type={showPw ? 'text' : 'password'}
                value={data.idNumber}
                onChange={(e) => patchData({ idNumber: e.target.value })}
                className="font-mono"
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="签发机关">
                <Input value={data.issuedBy ?? ''} onChange={(e) => patchData({ issuedBy: e.target.value })} />
              </Field>
              <Field label="有效期至">
                <Input
                  type={showPw ? 'text' : 'date'}
                  value={data.expiresAt ?? ''}
                  onChange={(e) => patchData({ expiresAt: e.target.value })}
                />
              </Field>
            </div>
            <Button size="sm" variant="ghost" onClick={() => setShowPw((v) => !v)}>
              {showPw ? '隐藏敏感字段' : '显示敏感字段'}
            </Button>
            <Field label="备注">
              <Textarea rows={2} value={data.note ?? ''} onChange={(e) => patchData({ note: e.target.value })} />
            </Field>
          </div>
        )}

        {data.type === 'WIFI' && (
          <div className="space-y-3">
            <Field label="SSID">
              <Input value={data.ssid} onChange={(e) => patchData({ ssid: e.target.value })} />
            </Field>
            <Field label="密码">
              <div className="flex gap-2">
                <Input
                  type={showPw ? 'text' : 'password'}
                  value={data.password}
                  onChange={(e) => patchData({ password: e.target.value })}
                  className="font-mono"
                />
                <Button size="sm" variant="ghost" onClick={() => setShowPw((v) => !v)}>
                  {showPw ? '隐藏' : '显示'}
                </Button>
              </div>
            </Field>
            <Field label="加密方式">
              <Select value={data.security ?? ''} onChange={(e) => patchData({ security: e.target.value })}>
                <option value="">自动</option>
                <option value="WPA2">WPA2</option>
                <option value="WPA3">WPA3</option>
                <option value="WEP">WEP</option>
                <option value="OPEN">开放</option>
              </Select>
            </Field>
            <Field label="备注">
              <Textarea rows={2} value={data.note ?? ''} onChange={(e) => patchData({ note: e.target.value })} />
            </Field>
          </div>
        )}
      </div>
    </Modal>
  );
}
